class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }

    // Buscador amplio (snippets/search-panel.liquid): teclado, sugerencias que
    // buscan dentro del panel y resaltado de lo que coincide.
    if (this.hasAttribute('data-panel')) {
      this.input.addEventListener('keydown', this.onPanelKeydown.bind(this));
      this.addEventListener('click', this.onPanelSuggestionClick.bind(this));
    }
  }

  get panelOptions() {
    return Array.from(this.querySelectorAll('[role="option"]')).filter((option) => option.offsetParent !== null);
  }

  setActiveOption(option) {
    this.panelOptions.forEach((item) => item.setAttribute('aria-selected', 'false'));
    if (!option) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  onPanelKeydown(event) {
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    const options = this.panelOptions;
    const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

    if (event.key === 'Enter') {
      if (current === -1) return;
      event.preventDefault();
      const link = options[current].querySelector('a[href]');
      if (link) link.click();
      return;
    }

    if (options.length === 0) return;
    event.preventDefault();
    let next;
    if (event.key === 'ArrowDown') {
      next = current === -1 ? 0 : (current + 1) % options.length;
    } else {
      next = current <= 0 ? options.length - 1 : current - 1;
    }
    this.setActiveOption(options[next]);
  }

  onPanelSuggestionClick(event) {
    const suggestion = event.target.closest('[data-search-term]');
    if (!suggestion || !this.contains(suggestion)) return;
    event.preventDefault();
    this.input.value = suggestion.dataset.searchTerm;
    this.input.focus();
    this.onChange();
  }

  highlightMatches(container) {
    const query = this.getQuery();
    if (!query) return;
    const normalize = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const terms = [query, ...query.split(/\s+/)]
      .map((term) => normalize(term.trim()))
      .filter((term, index, list) => term.length > 1 && list.indexOf(term) === index)
      .sort((a, b) => b.length - a.length);

    container.querySelectorAll('[data-highlight]').forEach((element) => {
      const chars = Array.from(element.textContent);
      const map = [];
      let normalized = '';
      chars.forEach((char, index) => {
        const normalizedChar = normalize(char);
        for (let i = 0; i < normalizedChar.length; i++) map.push(index);
        normalized += normalizedChar;
      });
      for (const term of terms) {
        const at = normalized.indexOf(term);
        if (at === -1) continue;
        const start = map[at];
        const end = map[at + term.length - 1] + 1;
        const mark = document.createElement('mark');
        mark.className = 'sp__hl';
        mark.textContent = chars.slice(start, end).join('');
        element.replaceChildren(chars.slice(0, start).join(''), mark, chars.slice(end).join(''));
        break;
      }
    });
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    this.abortPendingFetch();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    if (this.hasAttribute('data-panel')) {
      this.input.setAttribute('aria-expanded', 'false');
      this.setActiveOption(null);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    // The query is sent exactly as typed: appending search syntax (e.g. "-tag:...") makes Shopify
    // drop prefix matching, so partial words such as "bide" returned nothing. Shadow products are
    // filtered out in the Liquid snippet instead, which is why the API maximum (10) is requested
    // and the snippet trims the list down to the configured number of results.
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', 10),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    // Drop any in-flight request so a slower, older response can't overwrite newer results.
    this.abortPendingFetch();
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  abortPendingFetch() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    const controller = new AbortController();
    this.abortController = controller;

    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Cache the fetched data, but only render it if this is still the latest request
        this.cachedMap.set(url, responseText);
        if (controller.signal.aborted) return;
        this.renderSearchResults(responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Only the latest request may clear the loading state
        if (this.abortController !== controller) return;
        this.abortController = null;
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        if (this.hasAttribute('data-panel')) {
          this.setActiveOption(null);
          this.input.setAttribute('aria-expanded', 'true');
          this.highlightMatches(targetElement);
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }

  // El buscador amplio guarda sus recomendaciones en <template data-sp-deferred>
  // para no cargarlas en cada pagina: se insertan justo antes de abrirse.
  prepareToShow() {
    this.querySelectorAll('template[data-sp-deferred]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }
}
customElements.define('search-drawer', SearchDrawer);
